import React, { useEffect, useState } from 'react';
import { Loader2, Phone, TrendingDown, RotateCw, Home, Building, LineChart, Layers, Clock, GitBranch, Calendar, Shield, UserX, Hammer } from 'lucide-react';
import { clientApi, formatDate } from '../../components/client-portal/clientPortalApi';

const ICONS = {
  'trending-down': TrendingDown,
  'rotate-cw': RotateCw,
  'home': Home,
  'building': Building,
  'line-chart': LineChart,
  'layers': Layers,
  'clock': Clock,
  'git-branch': GitBranch,
  'calendar': Calendar,
  'shield': Shield,
  'user-x': UserX,
  'hammer': Hammer,
  'phone': Phone,
};

const STATUS_BADGES = {
  new: { label: 'Sent to your broker', cls: 'bg-amber-50 text-amber-800 ring-amber-200' },
  in_progress: { label: 'In progress', cls: 'bg-sky-50 text-sky-700 ring-sky-200' },
  scheduled: { label: 'Call scheduled', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-200' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  declined: { label: 'Declined', cls: 'bg-rose-50 text-rose-700 ring-rose-200' },
};

export default function ClientPostSettlement() {
  const [options, setOptions] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openOption, setOpenOption] = useState(null);
  const [notes, setNotes] = useState('');
  const [time, setTime] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [opts, reqs] = await Promise.all([
      clientApi('/client/post-settlement/options'),
      clientApi('/client/post-settlement/requests'),
    ]);
    setOptions(opts);
    setRequests(reqs);
  };

  useEffect(() => {
    (async () => { try { await reload(); } finally { setLoading(false); } })();
  }, []);

  const submit = async () => {
    if (!openOption) return;
    setBusy(true);
    try {
      await clientApi('/client/post-settlement/requests', {
        method: 'POST',
        body: { option_key: openOption.key, notes, preferred_time: time },
      });
      setOpenOption(null);
      setNotes('');
      setTime('');
      await reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="client-post-settlement">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">After settlement</h1>
        <p className="text-slate-500 mt-1 text-sm">Your loan journey doesn't end at settlement. Tap any option below whenever you need help.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {options.map((o) => {
          const Icon = ICONS[o.icon] || Phone;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => setOpenOption(o)}
              data-testid={`ps-option-${o.key}`}
              className="text-left rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-emerald-300 hover:shadow-md transition p-4 group"
            >
              <div className="w-10 h-10 rounded-lg bg-emerald-50 group-hover:bg-emerald-100 flex items-center justify-center text-emerald-600 mb-3">
                <Icon className="w-5 h-5" />
              </div>
              <div className="font-semibold text-slate-900">{o.title}</div>
              <div className="text-slate-500 text-sm mt-1">{o.description}</div>
            </button>
          );
        })}
      </div>

      {requests.length > 0 && (
        <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">Your requests</h2>
          <ul className="divide-y divide-slate-100">
            {requests.map((r) => {
              const badge = STATUS_BADGES[r.status] || STATUS_BADGES.new;
              return (
                <li key={r.id} className="py-3 flex items-start gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{r.option_title}</div>
                    {r.notes && <div className="text-slate-500 text-sm">{r.notes}</div>}
                    {r.broker_reply && (
                      <div className="mt-1.5 text-sm text-slate-700 bg-slate-50 ring-1 ring-slate-200 rounded-md p-2">
                        <span className="text-emerald-700 font-medium text-xs uppercase tracking-wide">Broker reply</span>
                        <div className="mt-0.5 whitespace-pre-wrap">{r.broker_reply}</div>
                      </div>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">Submitted {formatDate(r.created_at)}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full ring-1 text-[11px] whitespace-nowrap ${badge.cls}`}>{badge.label}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {openOption && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl ring-1 ring-slate-200 max-w-md w-full p-6 shadow-xl">
            <div className="font-semibold text-slate-900 text-lg">{openOption.title}</div>
            <div className="text-sm text-slate-500 mt-1">{openOption.description}</div>

            <input
              type="text"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="When works for you? (optional)"
              data-testid="ps-time"
              className="w-full mt-4 px-3 py-2 rounded-lg ring-1 ring-slate-200 text-sm bg-white"
            />
            <textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any background info you'd like to share?"
              data-testid="ps-notes"
              className="w-full mt-2 px-3 py-2 rounded-lg ring-1 ring-slate-200 text-sm bg-white"
            />

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setOpenOption(null)} className="px-4 py-2 text-slate-500 hover:text-slate-900 text-sm">Cancel</button>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="ps-submit"
                className="px-5 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Send to my broker
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
